import * as t from 'io-ts';

import { StatusC } from '@/vue/features/shared/record/schema/recordStatus';
import { RecordTypeC } from '@/vue/features/shared/record/schema/recordType';
import { HeavyTaskOutcomeC } from './HeavyTaskOutcome';
import { NotificationType } from './NotificationType';

const SenderC = t.type({
    id: t.number,
    name: t.string,
});

export const BaseNotificationC = t.type({
    id: t.string,
    receiverId: t.union([t.number, t.null]),
    sender: t.union([SenderC, t.null]),
    message: t.union([t.string, t.null]),
    createdAt: t.string,
    isViewed: t.boolean,
    isCleared: t.boolean,
});

const NotificationRecordC = t.type({
    id: t.string,
    name: t.string,
    type: RecordTypeC,
    status: StatusC,
    roomId: t.union([t.number, t.null]),
});

const NotificationCommentC = t.type({
    id: t.number,
    record: NotificationRecordC,
});

export const ReplyNotificationC = t.intersection([
    BaseNotificationC,
    t.type({
        type: t.literal(NotificationType.REPLY),
        comment: NotificationCommentC,
    }),
]);

export const SharingNotificationC = t.intersection([
    BaseNotificationC,
    t.type({
        type: t.literal(NotificationType.SHARING),
        record: NotificationRecordC,
    }),
]);

export const UploadLinkNotificationC = t.intersection([
    BaseNotificationC,
    t.type({
        type: t.literal(NotificationType.UPLOAD_LINK),
        record: NotificationRecordC,
    }),
]);

export const TagNotificationC = t.intersection([
    BaseNotificationC,
    t.type({
        type: t.literal(NotificationType.TAG),
        comment: NotificationCommentC,
        groupName: t.union([t.string, t.null]),
    }),
]);

const HeavyTaskFieldsC = t.type({
    record: NotificationRecordC,
    outcome: HeavyTaskOutcomeC,
    retryAvailable: t.boolean,
});

export const ConversionNotificationC = t.intersection([
    BaseNotificationC,
    HeavyTaskFieldsC,
    t.type({
        type: t.literal(NotificationType.CONVERSION),
    }),
]);

export const MultiviewNotificationC = t.intersection([
    BaseNotificationC,
    HeavyTaskFieldsC,
    t.type({
        type: t.literal(NotificationType.MULTIVIEW),
    }),
]);

export const TranscriptionNotificationC = t.intersection([
    BaseNotificationC,
    HeavyTaskFieldsC,
    t.type({
        type: t.literal(NotificationType.TRANSCRIPTION),
    }),
]);

export const AudioNotificationC = t.intersection([
    BaseNotificationC,
    HeavyTaskFieldsC,
    t.type({
        type: t.literal(NotificationType.AUDIO),
    }),
]);

export const HeavyTaskNotificationC = t.union([
    AudioNotificationC,
    ConversionNotificationC,
    MultiviewNotificationC,
    TranscriptionNotificationC,
]);

export const NotificationC = t.union([
    TagNotificationC,
    SharingNotificationC,
    UploadLinkNotificationC,
    ReplyNotificationC,
    AudioNotificationC,
    ConversionNotificationC,
    MultiviewNotificationC,
    TranscriptionNotificationC,
]);

export type ReplyNotification = t.TypeOf<typeof ReplyNotificationC>;
export type SharingNotification = t.TypeOf<typeof SharingNotificationC>;
export type UploadLinkNotification = t.TypeOf<typeof UploadLinkNotificationC>;
export type TagNotification = t.TypeOf<typeof TagNotificationC>;
export type ConversionNotification = t.TypeOf<typeof ConversionNotificationC>;
export type MultiviewNotification = t.TypeOf<typeof MultiviewNotificationC>;
export type TranscriptionNotification = t.TypeOf<typeof TranscriptionNotificationC>;
export type AudioNotification = t.TypeOf<typeof AudioNotificationC>;
export type HeavyTaskNotification = t.TypeOf<typeof HeavyTaskNotificationC>;
export type Notification = t.TypeOf<typeof NotificationC>;

const HEAVY_TASK_TYPES: ReadonlySet<Notification['type']> = new Set([
    NotificationType.AUDIO,
    NotificationType.CONVERSION,
    NotificationType.MULTIVIEW,
    NotificationType.TRANSCRIPTION,
]);

export const isHeavyTaskNotification = (
    notification: Notification,
): notification is HeavyTaskNotification => HEAVY_TASK_TYPES.has(notification.type);
